import React from "react";
import { gql, useQuery } from "@apollo/client";
import { Link, useParams } from "react-router-dom";
import {
    Alert,
    AlertTitle,
    Grid2,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {
    CardValidity,
    CustomerSavedCardFragment,
    NetsOperation,
    NetsProcessResponseFragment,
    NetsTransactionQuery,
    NetsTransactionQueryVariables,
    NetsTransactionType,
} from "../generated/gql/graphql";
import { Property, PropertyList } from "./ticket_v2/TicketPageHeader";
import { TimestampText } from "../widgets/date_time";
import { formatCustomerId, formatTicketId } from "../util/formatting";
import { formatMoney, NETS_TRANSACTION_FRAGMENT, TRANSACTION_TYPE_LABELS } from "./NetsTransactionsListPage";
import { NetsResponseCode } from "../widgets/NetsResponseCode";
import ScrollablePageStructure from "../widgets/ScrollablePageStructure";
import LoadingScreen from "../widgets/LoadingScreen";
import { usePageTitle } from "../util/page_title";
import { SendReceiptEmailButton } from "./ticket_v2/SendReceiptEmailButton";
import { JsonDiagnosticButton } from "../widgets/diagnostics";
import { TraceButton } from "../widgets/TraceButton";
import { NetsXmlButton } from "../widgets/NetsXmlButton";

export const NETS_TRANSACTION_PRE_AUTH_FRAGMENT = gql`
    fragment NetsTransactionPreAuth on NetsTransactionPreAuth {
        netsTransactionId

        amount
        amountAuthorized

        authResponse {
            ...NetsProcessResponse
        }

        createdTime
        authorizedTime
        finalizedTime
    }
`;

export const CUSTOMER_SAVED_CARDS_FRAGMENT = gql`
    fragment CustomerSavedCard on CustomerSavedCard {
        id
        displayName
        customer {
            id
            name
            phoneNumberFormatted
            createdTime
        }
        terminal
        transactionId {
            merchantId
            merchantEnvironment
            transactionId
        }
        cardDetails {
            issuer
            issuerId
            issuerCountry
            maskedPan
            expirationDate
        }
        verifyResponse {
            ...NetsProcessResponse
        }
        validity
        cardTokenExpiredTime
        createdTime
        transactions {
            ...NetsTransaction
        }
        transactionPreAuths {
            ...NetsTransactionPreAuth
        }
    }

    ${NETS_TRANSACTION_FRAGMENT}
    ${NETS_TRANSACTION_PRE_AUTH_FRAGMENT}
`;

const NETS_TRANSACTION_QUERY = gql`
    query NetsTransaction($id: String!) {
        netsTransaction(id: $id) {
            id
            netsTransactionId
            type
            savedCard {
                ...CustomerSavedCard
            }

            createdTime
            processedSuccessfullyTime
            amount
            amountRegistered
            amountCaptured
            canReleaseInvoices

            invoices {
                invoiceNumber
                ticket {
                    id
                    createdTime
                }
                totalAmount
                createdTime
            }
            paymentReconciliation {
                trace {
                    traceId
                    spanId
                    sampled
                }
            }
            credits {
                id

                creditResponse {
                    ...NetsProcessResponse
                }
                invoices {
                    invoiceNumber
                    ticket {
                        id
                        createdTime
                    }
                    totalAmount
                    createdTime
                }
                createdTime
            }

            captureResponse {
                ...NetsProcessResponse
            }
        }
    }
    ${CUSTOMER_SAVED_CARDS_FRAGMENT}
`;

type Transaction = NetsTransactionQuery["netsTransaction"];

function NetsTransactionPageHeader({ transaction }: { transaction: Transaction }) {
    const savedCard = transaction.savedCard as CustomerSavedCardFragment;
    const customer = savedCard.customer;

    return (
        <div>
            {savedCard.validity != CardValidity.Active ? (
                <Alert>
                    <AlertTitle>Deaktivert!</AlertTitle>
                    Kunden byttet kort etter denne kortbetalingen.
                </Alert>
            ) : null}
            <h2>Kortbetaling: {transaction.netsTransactionId}</h2>

            <div style={{ marginBottom: "20px" }}>
                <Stack direction="row" spacing={2}>
                    <SendReceiptEmailButton transactionId={transaction.id} />
                    <TraceButton trace={transaction.paymentReconciliation?.trace} />
                </Stack>
            </div>

            <Grid2 container spacing={2} direction={"row"}>
                <Grid2 size={{ md: 6 }}>
                    <Typography variant={"h6"} style={{ marginTop: "0" }}>
                        Betaling
                        <JsonDiagnosticButton name={"transaction"} value={transaction} />
                    </Typography>
                    <PropertyList>
                        <Property title={"Ordre-nummer"} value={transaction.id} />
                        <Property title={"Transaksjons-ID"} value={transaction.netsTransactionId} />
                        <Property
                            title={"Metode"}
                            value={TRANSACTION_TYPE_LABELS[transaction.type as NetsTransactionType]}
                        />
                        <Property title={"Beløp"} value={formatMoney(transaction.amount)} />
                        <Property
                            title={"Respons-kode"}
                            value={
                                <NetsResponseCode
                                    operation={NetsOperation.Capture}
                                    response={transaction.captureResponse as NetsProcessResponseFragment}
                                />
                            }
                        />
                        <Property title={"Tidspunkt"} value={<TimestampText value={transaction.createdTime} />} />
                        <Property
                            requiresSuperUser
                            title={"amountRegistered"}
                            value={formatMoney(transaction.amountRegistered)}
                        />
                        <Property
                            requiresSuperUser
                            title={"amountCaptured"}
                            value={formatMoney(transaction.amountCaptured)}
                        />
                        <Property
                            requiresSuperUser
                            title={"processedSuccessfullyTime"}
                            value={<TimestampText value={transaction.processedSuccessfullyTime} />}
                        />
                        <Property
                            requiresSuperUser
                            title={"canReleaseInvoices"}
                            value={transaction.canReleaseInvoices}
                        />
                    </PropertyList>

                    <Typography variant={"h6"} style={{ marginTop: "30px" }}>
                        Kunde
                        <JsonDiagnosticButton name={"customer"} value={customer} />
                    </Typography>
                    <PropertyList>
                        <Property
                            title={"App-Id"}
                            value={<Link to={"/customer/" + customer.id}>{formatCustomerId(customer.id)}</Link>}
                        />
                        <Property title={"Mobilnummer"} value={customer.phoneNumberFormatted} />
                        {customer.name ? <Property title={"Navn"} value={customer.name} /> : null}
                        <Property title={"Registrert"} value={<TimestampText value={customer.createdTime} />} />
                    </PropertyList>
                </Grid2>
                <Grid2 size={{ md: 6 }}>
                    <Typography variant={"h6"} style={{ marginTop: "0" }}>
                        Kort
                        <NetsXmlButton savedCardId={savedCard.id} style={{ marginLeft: "20px", marginRight: "10px" }} />
                        <JsonDiagnosticButton name={"savedCard"} value={savedCard} />
                    </Typography>
                    <CustomerSavedCardPropertyList savedCard={savedCard} />
                </Grid2>
            </Grid2>
        </div>
    );
}

const NetsTransactionPage = React.memo(() => {
    const params = useParams<{ transactionId: string }>();
    const transactionId = params.transactionId!;
    const query = useQuery<NetsTransactionQuery, NetsTransactionQueryVariables>(NETS_TRANSACTION_QUERY, {
        variables: { id: transactionId },
    });

    usePageTitle("Kortbetaling - " + transactionId);

    if (query.loading) return <LoadingScreen />;

    const transaction = query.data!.netsTransaction;
    const invoices = transaction.invoices;

    return (
        <ScrollablePageStructure style={{ padding: "20px" }}>
            <NetsTransactionPageHeader transaction={transaction} />

            <div>
                <NetsXmlButton transactionId={transactionId} />
            </div>

            <Typography variant={"h6"} style={{ marginTop: "30px" }}>
                Fakturaer
            </Typography>
            <div style={{ width: "fit-content" }}>
                <Table width={"auto"}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Løpenummer</TableCell>
                            <TableCell>Billett-Id</TableCell>
                            <TableCell>Kjøpt</TableCell>
                            <TableCell>Faktura endret dato</TableCell>
                            <TableCell align={"right"}>Beløp</TableCell>
                            <TableCell align={"right"} />
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {invoices.map((invoice) => (
                            <TableRow key={invoice.invoiceNumber}>
                                <TableCell>{invoice.invoiceNumber}</TableCell>
                                <TableCell>
                                    <Link to={"/ticket/" + invoice.ticket.id}>{formatTicketId(invoice.ticket.id)}</Link>
                                </TableCell>
                                <TableCell>
                                    <TimestampText value={invoice.ticket.createdTime} />
                                </TableCell>
                                <TableCell>
                                    <TimestampText value={invoice.createdTime} />
                                </TableCell>
                                <TableCell align={"right"}>{formatMoney(invoice.totalAmount)}</TableCell>
                                <TableCell align={"right"}>
                                    <JsonDiagnosticButton name={"invoice"} value={invoice} />
                                </TableCell>
                            </TableRow>
                        ))}
                        {transaction.credits.map((credit) => {
                            return credit.invoices.map((invoice) => {
                                return (
                                    <TableRow key={credit.id}>
                                        <TableCell>{invoice.invoiceNumber}</TableCell>
                                        <TableCell>
                                            <Link to={"/ticket/" + invoice.ticket.id}>
                                                {formatTicketId(invoice.ticket.id)}
                                            </Link>
                                        </TableCell>
                                        <TableCell>
                                            <TimestampText value={invoice.ticket.createdTime} />
                                        </TableCell>
                                        <TableCell>
                                            <TimestampText value={invoice.createdTime} />
                                        </TableCell>
                                        <TableCell align={"right"}>{formatMoney(invoice.totalAmount)}</TableCell>
                                        <TableCell>
                                            <NetsResponseCode
                                                operation={NetsOperation.Credit}
                                                response={credit.creditResponse as NetsProcessResponseFragment}
                                            />
                                        </TableCell>
                                        <TableCell align={"right"}>
                                            <JsonDiagnosticButton name={"credit"} value={credit} />
                                        </TableCell>
                                    </TableRow>
                                );
                            });
                        })}
                    </TableBody>
                </Table>
            </div>
        </ScrollablePageStructure>
    );
});

export { NetsTransactionPage };

export function CustomerSavedCardPropertyList({ savedCard }: { savedCard: CustomerSavedCardFragment }) {
    const cardDetails = savedCard.cardDetails;
    return (
        <PropertyList>
            <Property requiresSuperUser title={"Id"} value={savedCard.id} />
            {cardDetails ? (
                <>
                    <Property title={"Kortnummer"} value={cardDetails.maskedPan} />
                    <Property title={"Utsteder"} value={cardDetails.issuer} />
                    <Property title={"Utsteder-ID"} value={cardDetails.issuerId} />
                    <Property title={"Utsteder land"} value={cardDetails.issuerCountry} />
                    <Property title={"Utløpsdato"} value={cardDetails.expirationDate} />
                </>
            ) : null}

            <Typography variant={"overline"} style={{ marginTop: "10px" }}>
                Feltene nedenfor omhandler kort-registreringen.
            </Typography>
            <Property title={"Ordre-nummer"} value={savedCard.id} />
            <Property title={"Nets transaksjons-id"} value={savedCard.transactionId.transactionId} />
            <Property requiresSuperUser title={"Nets merchant id"} value={savedCard.transactionId.merchantId} />
            <Property title={"Registrert"} value={<TimestampText value={savedCard.createdTime} />} />
        </PropertyList>
    );
}
